import { enableDataExportUsageEnhancements, planUsageTabsOverride } from '@gonfalon/dogfood-flags/dogfood-flags';

import { TabVisibility, USAGE_UNLIMITED, usageTabConfig } from '../../constants';
import type { PlanLimitsType, Subscription, UsageTabKey } from '../../types';
import { UsageTab } from '../../types';

import { hasSubscriptionLimit } from './hasSubscriptionLimit';

export type TabOverrideKey = keyof ReturnType<typeof planUsageTabsOverride>;

export enum TabOverrideValue {
  PLAN_ENABLED = 'planEnabled',
  PLAN_DISABLED = 'planDisabled',
  DIAGNOSTIC_ENABLED = 'diagnosticEnabled',
  DIAGNOSTIC_DISABLED = 'diagnosticDisabled',
}

export const tabKeyToOverrideKey: Record<UsageTabKey, TabOverrideKey> = {
  [UsageTab.SERVICE_CONNECTIONS]: 'serviceConnectionsTab',
  [UsageTab.CLIENT_SIDE_MAU]: 'clientSideMAUTab',
  [UsageTab.EXPERIMENTATION_KEYS]: 'experimentationKeysTab',
  [UsageTab.EXPERIMENTATION_EVENTS]: 'experimentationEventsTab',
  [UsageTab.EXPERIMENTATION_MAU]: 'experimentationMAUTab',
  [UsageTab.DATA_EXPORT]: 'dataExportTab',
  [UsageTab.DATA_EXPORT_COMBINED]: 'dataExportCombinedTab',
  [UsageTab.WAREHOUSE_DATA_EXPORT]: 'warehouseDataExportTab',
  [UsageTab.SESSIONS]: 'observabilitySessionsTab',
  [UsageTab.ERRORS]: 'observabilityErrorsTab',
  [UsageTab.LOGS]: 'observabilityLogsTab',
  [UsageTab.TRACES]: 'observabilityTracesTab',
  [UsageTab.METRICS]: 'observabilityMetricsTab',
  [UsageTab.VEGA_AI]: 'vegaAITab',
  // The override key for the AI Runs tab is intentionally `aiExecutionsTab` for
  // backwards compatibility with existing flag targeting. The tab itself was
  // renamed from AI Executions to AI Runs.
  [UsageTab.AI_RUNS]: 'aiExecutionsTab',
  [UsageTab.TOTAL_CONTEXTS]: 'totalContextsTab',
  [UsageTab.CLIENT_SIDE_CONTEXTS]: 'clientSideContextsTab',
  [UsageTab.SERVER_SIDE_CONTEXTS]: 'serverSideContextsTab',
  [UsageTab.CLIENT_CONNECTIONS]: 'clientConnectionsTab',
  [UsageTab.PEAK_CONCURRENT_CONNECTIONS]: 'peakConcurrentConnectionsTab',
};

export type VisibleUsageTabsOptions = {
  hasWarehouseDataExport?: boolean;
};

export function isDataExportCombinedTabEnabled(): boolean {
  return (
    !!enableDataExportUsageEnhancements() &&
    !(planUsageTabsOverride()?.dataExportCombinedTab ?? []).includes(TabOverrideValue.PLAN_DISABLED)
  );
}

function getPlanUsageTabs(
  limits: Subscription['_limits'] | undefined,
  { hasWarehouseDataExport = false }: VisibleUsageTabsOptions,
): Set<UsageTabKey> {
  const visibleTabs = new Set<UsageTabKey>([]);

  if (!limits) {
    return visibleTabs;
  }

  const overrideConfig = planUsageTabsOverride();

  for (const [tab, config] of Object.entries(usageTabConfig)) {
    const override = overrideConfig?.[tabKeyToOverrideKey[tab as UsageTabKey]] ?? [];

    if (tab === UsageTab.DATA_EXPORT_COMBINED) {
      if (
        isDataExportCombinedTabEnabled() &&
        (hasWarehouseDataExport ||
          hasSubscriptionLimit((limits as PlanLimitsType)[usageTabConfig[UsageTab.DATA_EXPORT_COMBINED].limitName]))
      ) {
        visibleTabs.add(UsageTab.DATA_EXPORT_COMBINED);
      }
      continue;
    }

    // Add hidden tabs via flag override for testing
    if (config.visibility === TabVisibility.HIDDEN) {
      if (override.includes(TabOverrideValue.PLAN_ENABLED)) {
        visibleTabs.add(tab as UsageTabKey);
      }
      continue;
    }

    if (config.visibility !== TabVisibility.PLAN_ONLY && config.visibility !== TabVisibility.PLAN_OR_DIAGNOSTIC) {
      continue;
    }

    if (!config.limitName) {
      continue;
    }

    const limit = (limits as PlanLimitsType)[config.limitName];
    const hasLimit = limit && (limit > 0 || limit === USAGE_UNLIMITED);

    // Add tab if the metric is provisioned on the subscription
    if (hasLimit && !override.includes(TabOverrideValue.PLAN_DISABLED)) {
      visibleTabs.add(tab as UsageTabKey);
      continue;
    }

    // Force visibility if flag is true, regardless of limit
    if (override.includes(TabOverrideValue.PLAN_ENABLED)) {
      visibleTabs.add(tab as UsageTabKey);
    }
  }

  return visibleTabs;
}

export function getVisibleUsageTabs(
  limits?: Subscription['_limits'],
  options: VisibleUsageTabsOptions = {},
): UsageTabKey[] {
  const visibleTabs = getPlanUsageTabs(limits, options);

  if (visibleTabs.has(UsageTab.DATA_EXPORT_COMBINED)) {
    visibleTabs.delete(UsageTab.DATA_EXPORT);
  }

  return Array.from(visibleTabs);
}

export function getTabsReplacedByCombined(
  limits?: Subscription['_limits'],
  options: VisibleUsageTabsOptions = {},
): UsageTabKey[] {
  const visibleTabs = getPlanUsageTabs(limits, options);
  return visibleTabs.has(UsageTab.DATA_EXPORT_COMBINED) && visibleTabs.has(UsageTab.DATA_EXPORT)
    ? [UsageTab.DATA_EXPORT]
    : [];
}
